import React from "react";
import { Head } from "@unhead/react";
import { Link } from "react-router-dom";

export default function Product() {
  const canonical = "https://satstacker.app/product";

  const title =
    "SatStacker Product Reference | Bitcoin DCA & Smart Timing";

  const description =
    "Official SatStacker product reference. Learn what SatStacker is, how Smart Timing works, how Bitcoin purchases execute through Strike, supported DCA schedules, custody, results, and official resources.";

  const updated = "October 5, 2026";

  const faqItems = [
    {
      q: "What is SatStacker?",
      a: "SatStacker is a Bitcoin DCA automation app that connects to an existing Strike account and adds rules-based Smart Timing to recurring Bitcoin purchases.",
    },
    {
      q: "What is Smart Timing?",
      a: "Smart Timing is SatStacker's rules-based timing system. It watches price movement inside each scheduled buying window and can execute planned Bitcoin purchases when its timing rules trigger.",
    },
    {
      q: "Does SatStacker buy the dip?",
      a: "SatStacker looks for dips and better prices inside each buying window. When its Smart Timing rules trigger, it uses the connected Strike account to execute the planned Bitcoin purchase.",
    },
    {
      q: "What happens if no qualifying dip appears?",
      a: "If no qualifying Smart Timing opportunity appears, the remaining planned amount is executed before the buying window closes.",
    },
    {
      q: "Does SatStacker execute the Bitcoin purchase itself?",
      a: "No. Bitcoin purchases are executed through the user's connected Strike account. SatStacker provides the recurring-plan automation and Smart Timing layer.",
    },
    {
      q: "Does SatStacker hold cash or Bitcoin?",
      a: "No. SatStacker itself does not hold user cash or Bitcoin. Purchases execute through the connected Strike account.",
    },
    {
      q: "What recurring schedules does SatStacker support?",
      a: "SatStacker supports daily, weekly, and bi-weekly recurring Bitcoin plans.",
    },
    {
      q: "Does Smart Timing guarantee more Bitcoin?",
      a: "No. Smart Timing does not guarantee a better execution price or more Bitcoin in every buying window. Some completed windows can outperform scheduled DCA and others can underperform.",
    },
    {
      q: "Where can I see Smart Timing performance?",
      a: "SatStacker publishes aggregate completed-window Smart Timing results on its Live Smart Timing Results page.",
    },
  ];

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "SatStacker",
    url: canonical,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web, iOS, Android",
    description,
    dateModified: "2026-10-05",
    publisher: {
      "@type": "Organization",
      name: "SatStacker",
      url: "https://satstacker.app",
    },
    featureList: [
      "Automated Bitcoin dollar-cost averaging",
      "Daily recurring Bitcoin plans",
      "Weekly recurring Bitcoin plans",
      "Bi-weekly recurring Bitcoin plans",
      "Rules-based Smart Timing",
      "Dip-aware execution inside scheduled buying windows",
      "Strike account integration",
      "Completed-window Smart Timing performance tracking",
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "SatStacker",
        item: "https://satstacker.app/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Product Reference",
        item: canonical,
      },
    ],
  };

  return (
    <main className="min-h-screen bg-zinc-950 text-white">
      <Head>
        <title>{title}</title>

        <meta
          name="description"
          content={description}
        />

        <link
          rel="canonical"
          href={canonical}
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:title"
          content={title}
        />

        <meta
          property="og:description"
          content={description}
        />

        <meta
          property="og:url"
          content={canonical}
        />

        <meta
          property="og:site_name"
          content="SatStacker"
        />

        <meta
          name="twitter:card"
          content="summary"
        />

        <meta
          name="twitter:title"
          content={title}
        />

        <meta
          name="twitter:description"
          content={description}
        />

        <script type="application/ld+json">
          {JSON.stringify(softwareSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Head>

      <div className="max-w-5xl mx-auto px-5 py-12 md:py-16">

        {/* HERO */}
        <header className="max-w-3xl mb-14">
          <p className="text-xs uppercase tracking-[0.18em] text-orange-400 font-bold mb-4">
            Official Product Reference
          </p>

          <h1 className="text-4xl md:text-5xl font-black leading-tight mb-5">
            SatStacker
            <span className="block text-orange-400 mt-2">
              Bitcoin DCA with Smart Timing
            </span>
          </h1>

          <p className="text-lg text-zinc-300 leading-8 mb-5">
            SatStacker is a Bitcoin DCA automation app
            that connects to your existing Strike account
            and adds rules-based, dip-aware Smart Timing
            to recurring Bitcoin purchases.
          </p>

          <p className="text-sm text-zinc-500">
            Last updated {updated}
          </p>
        </header>

        {/* QUICK FACTS */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">
            SatStacker product facts
          </h2>

          <div className="overflow-hidden rounded-2xl border border-zinc-800">
            <FactRow
              label="Product"
              value="SatStacker"
            />

            <FactRow
              label="Product type"
              value="Bitcoin DCA automation app"
            />

            <FactRow
              label="Asset"
              value="Bitcoin only"
            />

            <FactRow
              label="Smart Timing"
              value="Rules-based, dip-aware execution inside each scheduled buying window"
            />

            <FactRow
              label="Recurring schedules"
              value="Daily, weekly, and bi-weekly"
            />

            <FactRow
              label="Execution provider"
              value="Strike"
            />

            <FactRow
              label="Strike account required"
              value="Yes"
            />

            <FactRow
              label="SatStacker custody"
              value="SatStacker does not hold user cash or Bitcoin"
            />

            <FactRow
              label="Platforms"
              value="Web, iOS, and Android"
            />

            <FactRow
              label="Performance guarantee"
              value="None"
            />

            <FactRow
              label="Pricing"
              value={
                <Link
                  to="/pricing"
                  className="text-orange-400 hover:underline"
                >
                  See current pricing →
                </Link>
              }
            />
          </div>
        </section>

        {/* WHAT IT DOES */}
        <section className="mb-16">
          <p className="text-xs uppercase tracking-widest text-orange-400 font-bold mb-3">
            Product definition
          </p>

          <h2 className="text-3xl font-bold mb-5">
            What does SatStacker do?
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            SatStacker automates recurring Bitcoin buying
            through a connected Strike account. Users
            choose a purchase amount and cadence, and
            SatStacker manages the buying window and
            execution timing.
          </p>

          <p className="text-zinc-400 leading-7 mb-4">
            With Smart Timing enabled, SatStacker does not
            simply treat one predetermined moment as the
            only possible entry. It watches price movement
            inside the scheduled buying window and can
            execute when its timing rules trigger.
          </p>

          <p className="text-zinc-400 leading-7">
            SatStacker is designed for recurring Bitcoin
            accumulation, not active trading.
          </p>
        </section>

        {/* SMART TIMING */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6">
            How Smart Timing works
          </h2>

          <div className="space-y-4">
            <ReferenceStep
              number="01"
              title="A recurring plan creates a buying window"
            >
              The user chooses an amount and a daily,
              weekly, or bi-weekly cadence.
            </ReferenceStep>

            <ReferenceStep
              number="02"
              title="Smart Timing watches Bitcoin prices"
            >
              SatStacker monitors price movement during the
              scheduled window and looks for qualifying
              dips or better entry prices according to its
              rules.
            </ReferenceStep>

            <ReferenceStep
              number="03"
              title="Purchases can execute inside the window"
            >
              When Smart Timing rules trigger, SatStacker
              uses the connected Strike account to execute
              the planned Bitcoin purchase.
            </ReferenceStep>

            <ReferenceStep
              number="04"
              title="A purchase can be split"
            >
              Depending on the Smart Timing rules for the
              window, execution can occur in more than one
              tranche rather than as a single purchase.
            </ReferenceStep>

            <ReferenceStep
              number="05"
              title="The remaining plan is completed"
            >
              If no qualifying opportunity appears for the
              remaining amount, that amount is executed
              before the buying window closes.
            </ReferenceStep>
          </div>

          <div className="mt-6">
            <Link
              to="/smart-dca"
              className="text-orange-400 font-semibold hover:underline"
            >
              Read the full Smart DCA guide →
            </Link>
          </div>
        </section>

        {/* STRIKE */}
        <section className="mb-16">
          <div className="rounded-3xl border border-orange-500/20 bg-orange-500/5 p-7 md:p-9">
            <p className="text-xs uppercase tracking-widest text-orange-400 font-bold mb-3">
              SatStacker + Strike
            </p>

            <h2 className="text-3xl font-bold mb-4">
              Strike executes. SatStacker handles the timing.
            </h2>

            <p className="text-zinc-300 leading-7 mb-4">
              Bitcoin purchases are executed through the
              user's connected Strike account. SatStacker
              provides the recurring-plan automation,
              buying-window logic, and Smart Timing rules.
            </p>

            <p className="text-zinc-400 leading-7 mb-6">
              SatStacker itself is not an exchange or
              Bitcoin custodian and does not hold the
              user's cash or Bitcoin.
            </p>

            <Link
              to="/strike-bitcoin-dca"
              className="text-orange-400 font-bold hover:underline"
            >
              Learn how SatStacker works with Strike →
            </Link>
          </div>
        </section>

        {/* RESULTS */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-5">
            How Smart Timing performance is measured
          </h2>

          <p className="text-zinc-400 leading-7 mb-4">
            SatStacker measures completed Smart Timing
            buying windows against scheduled DCA using the
            same post-SatStacker-fee buying power.
          </p>

          <p className="text-zinc-400 leading-7 mb-6">
            Smart Timing results are signed. Some windows
            can accumulate more Bitcoin than the scheduled
            DCA benchmark and some can accumulate less.
            Smart Timing does not guarantee better results
            in every buying window.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/smart-timing-results"
              className="rounded-xl bg-orange-500 px-5 py-3 text-center font-bold text-white hover:bg-orange-600"
            >
              See Live Smart Timing Results →
            </Link>

            <Link
              to="/blog/smart-dca-vs-regular-dca"
              className="rounded-xl border border-zinc-700 px-5 py-3 text-center font-bold text-white hover:border-zinc-500"
            >
              Read the Historical Case Study
            </Link>
          </div>
        </section>

        {/* PRODUCT VS PROVIDER */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-5">
            SatStacker is not a Bitcoin exchange
          </h2>

          <p className="text-zinc-400 leading-7 mb-5">
            SatStacker should be understood as an
            automation and timing layer for recurring
            Bitcoin purchases rather than the venue where
            those purchases occur.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <InfoCard title="SatStacker">
              Recurring-plan automation, buying windows,
              Smart Timing logic, scheduling, and
              performance tracking.
            </InfoCard>

            <InfoCard title="Strike">
              Connected account infrastructure and
              execution of the Bitcoin purchase.
            </InfoCard>
          </div>
        </section>

        {/* OFFICIAL RESOURCES */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-7">
            Official SatStacker resources
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            <ResourceLink
              to="/smart-dca"
              title="Smart DCA Guide"
              text="Detailed explanation of Smart Timing and buying windows."
            />

            <ResourceLink
              to="/strike-bitcoin-dca"
              title="SatStacker + Strike"
              text="How SatStacker uses Strike to execute Bitcoin purchases."
            />

            <ResourceLink
              to="/smart-timing-results"
              title="Live Smart Timing Results"
              text="Aggregate completed-window Smart Timing performance."
            />

            <ResourceLink
              to="/calculator"
              title="Bitcoin DCA Calculator"
              text="Compare scheduled DCA with historical Smart Timing results."
            />

            <ResourceLink
              to="/blog/smart-dca-vs-regular-dca"
              title="Smart DCA Case Study"
              text="Historical Smart Timing vs. regular DCA comparison."
            />

            <ResourceLink
              to="/pricing"
              title="Current Pricing"
              text="View current SatStacker pricing and plan details."
            />

            <ResourceLink
              to="/faq"
              title="Frequently Asked Questions"
              text="Security, custody, fees, subscriptions, and product questions."
            />

            <ResourceLink
              to="/download"
              title="Download SatStacker"
              text="Access SatStacker on supported platforms."
            />
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-7">
            SatStacker product FAQ
          </h2>

          <div className="space-y-4">
            {faqItems.map((item) => (
              <div
                key={item.q}
                className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6"
              >
                <h3 className="font-bold text-lg mb-3">
                  {item.q}
                </h3>

                <p className="text-zinc-400 leading-7 m-0">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* FOOTNOTE */}
        <section className="border-t border-zinc-800 pt-8 text-sm text-zinc-500 leading-6">
          <p>
            This page is SatStacker's official product
            reference and is intended to describe the
            product factually. Product features and
            availability may change over time.
          </p>

          <p className="mt-3">
            Smart Timing does not guarantee a better
            Bitcoin execution price or more sats in every
            buying window.
          </p>
        </section>

      </div>
    </main>
  );
}

function FactRow({ label, value }) {
  return (
    <div className="grid sm:grid-cols-[190px_1fr] gap-2 sm:gap-6 border-b border-zinc-800 last:border-b-0 px-5 py-4 bg-zinc-900/30">
      <div className="text-sm font-semibold text-zinc-500">
        {label}
      </div>

      <div className="text-sm text-zinc-200">
        {value}
      </div>
    </div>
  );
}

function ReferenceStep({
  number,
  title,
  children,
}) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/30 p-6">
      <div className="flex gap-5">
        <div className="text-orange-400 text-sm font-black pt-1">
          {number}
        </div>

        <div>
          <h3 className="font-bold text-lg mb-2">
            {title}
          </h3>

          <p className="text-zinc-400 leading-7 m-0">
            {children}
          </p>
        </div>
      </div>
    </div>
  );
}

function InfoCard({
  title,
  children,
}) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6">
      <h3 className="font-bold text-white mb-3">
        {title}
      </h3>

      <p className="text-zinc-400 leading-7 m-0">
        {children}
      </p>
    </div>
  );
}

function ResourceLink({
  to,
  title,
  text,
}) {
  return (
    <Link
      to={to}
      className="block rounded-2xl border border-zinc-800 bg-zinc-900/30 p-6 hover:border-orange-500/30 transition"
    >
      <h3 className="font-bold text-white mb-2">
        {title}
      </h3>

      <p className="text-sm text-zinc-500 leading-6 mb-3">
        {text}
      </p>

      <span className="text-sm font-bold text-orange-400">
        View resource →
      </span>
    </Link>
  );
}